import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { form, FormField as SignalFormField, required, schema } from '@angular/forms/signals';
import {
  SignalCheckbox, SignalDropdown, SignalFormDirective,
  SignalNumberField,
  SignalTextField,
} from '@vallift/ngl';
import type { FormField, TextareaResize, TextareaRows } from '../../../../models/built-form';

interface TextareaConfigModel {
  label: string;
  placeholder: string;
  defaultValue: string;
  helpText: string;
  rows: TextareaRows;
  resize: TextareaResize;
  characterLimit: number | null;
  showCharacterCounter: boolean;
  required: boolean;
  minLength: number | null;
  maxLength: number | null;
  pattern: string;
  customMessage: string;
}

interface SelectOption<T> {
  label: string;
  value: T;
}

@Component({
  selector: 'app-textarea-config-view',
  standalone: true,
  imports: [
    SignalFormField,
    SignalTextField,
    SignalNumberField,
    SignalCheckbox,
    SignalDropdown,
    SignalFormDirective,
  ],
  templateUrl: './textarea-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextareaConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly model = signal<TextareaConfigModel>({
    label: '',
    placeholder: '',
    helpText: '',
    defaultValue: '',
    rows: 3,
    resize: 'none',
    characterLimit: null,
    showCharacterCounter: false,
    required: false,
    minLength: null,
    maxLength: null,
    pattern: '',
    customMessage: '',
  });

  readonly textareaRowsOptions: SelectOption<TextareaRows>[] = [
    { label: 'Small', value: 3 },
    { label: 'Medium', value: 5 },
    { label: 'Large', value: 8 },
    { label: 'Extra Large', value: 10 },
  ];

  readonly textareaResizeOptions: SelectOption<TextareaResize>[] = [
    { label: 'None', value: 'none' },
    { label: 'Vertical', value: 'vertical' },
    { label: 'Horizontal', value: 'horizontal' },
    { label: 'Both', value: 'both' },
  ];

  readonly configForm = form(this.model,(schema=>{
    required(schema.label)
  }));

  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      const validation = next.validation ?? {};
      this.model.set({
        label: next.label,
        placeholder: next.placeholder ?? '',
        defaultValue:
          typeof next.config?.defaultValue === 'string'
            ? next.config.defaultValue
            : '',
        helpText: next.helpText ?? '',
        rows: next.textareaRows ?? 3,
        resize: next.textareaResize ?? 'none',
        characterLimit: next.textareaCharacterLimit ?? null,
        showCharacterCounter: next.textareaShowCharacterCounter ?? false,
        required: next.required ?? validation.required ?? false,
        minLength: validation.minLength ?? null,
        maxLength: validation.maxLength ?? null,
        pattern: validation.pattern ?? '',
        customMessage: validation.customMessage ?? '',
      });
    });

    effect(() => {
      const draft = this.model();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }
      this.fieldChange.emit({
        ...field,
        label: draft.label.trim(),
        placeholder: draft.placeholder.trim() || undefined,
        config: {
          ...field.config,
          defaultValue: draft.defaultValue,
        },
        helpText: draft.helpText.trim() || undefined,
        textareaRows: draft.rows,
        textareaResize: draft.resize,
        textareaCharacterLimit: draft.characterLimit ?? undefined,
        textareaShowCharacterCounter: draft.showCharacterCounter,
        required: draft.required,
        validation: {
          required: draft.required,
          minLength: draft.minLength ?? undefined,
          maxLength: draft.maxLength ?? undefined,
          pattern: draft.pattern.trim() || undefined,
          customMessage: draft.customMessage.trim() || undefined,
        },
        options: undefined,
        headingLevel: undefined,
        spacerSize: undefined,
      });
    });
  }
}
